import React, { Component, createRef } from 'react';

// CSS
import '../Subscriptions/Subscription.scss';
import './Billing.scss';

// Helper
import { checkCookie } from '../../Helper/Cookies';
import { apiFetch, platformInfo } from '../../Helper/Api';
import { usd, egp, day, gb, STATE_LABEL, STATE_SEVERITY, REQUEST_LABEL, REQUEST_SEVERITY, TX_LABEL } from './format';

// Components
import Header from '../../components/Header/Header';
import Calculator from './Calculator';
import TopUpDialog from './TopUpDialog';
import { Confirm } from '../../components/UI/Overlays';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { InputSwitch } from 'primereact/inputswitch';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';

// Properties
const properties = require('../../properties.json');

const reduceMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// format.js speaks PrimeReact's severities; the page's chips are the standard set's
const CHIP = { success: 'is-ok', warning: 'is-wait', danger: 'is-bad', info: 'is-off' };
const Chip = ({ label, severity }) => <span className={`ui-chip ${CHIP[severity] || 'is-off'}`}><span className="dot" />{label}</span>;

/** A money figure that counts to its value once, when it first appears. */
class CountUp extends Component {
    state = { v: 0 };

    componentDidMount() { this.run(0); }
    componentDidUpdate(prev) { if (prev.to !== this.props.to) this.run(this.state.v); }
    componentWillUnmount() { if (this.raf) cancelAnimationFrame(this.raf); }

    run = (from) => {
        const to = Number(this.props.to) || 0;
        if (this.raf) cancelAnimationFrame(this.raf);
        if (reduceMotion()) { this.setState({ v: to }); return; }
        const start = performance.now();
        const tick = (t) => {
            const k = Math.min(1, (t - start) / 1000);
            this.setState({ v: from + (to - from) * (1 - Math.pow(1 - k, 3)) });
            if (k < 1) this.raf = requestAnimationFrame(tick);
        };
        this.raf = requestAnimationFrame(tick);
    };

    render() { return this.props.format(this.state.v); }
}

// "My subscription": the calculator (to subscribe), where the subscription stands, renewing, auto-renew, asking
// for more, the wallet and every request / movement.
class Billing extends Component {
    constructor(props) {
        super(props);
        this.toast = createRef(null);
    }

    state = {
        loading: true,
        status: null,
        config: null,
        supportWhatsapp: '',
        transactions: [],
        topUps: [],
        upgrades: [],
        showCalculator: false,
        topUpOpen: false,
        topUpAmount: null,
        renewMonths: 1,
        renewPromo: '',
        renewing: false,
        startingTrial: false,
        upgradeOpen: false,
        upgrade: { addStorageGb: 0, addStudents: 0, addVideos: 0, note: '' },
        upgradeQuote: null,
        upgradeError: null,
        upgradeSending: false,
        agent: null,          // GET /teachers/me/agent: { linked, code, canAdd }
        agentCode: '',
        agentSaving: false,
        tab: 0,               // which history list is showing
        ask: null,            // the open confirmation: { title, note, icon, confirmLabel, busyKey, run }
    }

    componentDidMount() {
        document.title = properties.site_name + " | اشتراكي";
        if (!checkCookie('name')) {
            window.localStorage.removeItem('currentTeacher');
            window.location.href = `/`;
            return;
        }
        platformInfo().then(info => this.setState({ supportWhatsapp: (info && info.supportWhatsapp) || '' })).catch(() => {});
        this.load();
        this.loadAgent();
    }

    loadAgent = () => apiFetch('/teachers/me/agent').then(agent => this.setState({ agent })).catch(() => {});

    saveAgent = () => {
        const code = this.state.agentCode.trim();
        if (!code) return;
        this.setState({
            ask: {
                title: 'تربط حسابك بالوكيل ده؟',
                note: `هتربط حسابك بالوكيل صاحب الكود ${code}. الكود مش هيتغير بعد كده.`,
                icon: 'pi pi-user-plus',
                confirmLabel: 'أيوه، اربط',
                busyKey: 'agentSaving',
                // the code is the one read at the click, as the old confirm() had it
                run: () => this.doSaveAgent(code),
            },
        });
    };

    doSaveAgent = (code) => {
        this.setState({ agentSaving: true });
        apiFetch('/teachers/me/agent', { method: 'PUT', body: { code } })
            .then(agent => { this.setState({ agent, agentCode: '', ask: null }); this.ok('تم ربط حسابك بالوكيل'); })
            .catch(e => { this.setState({ ask: null }); this.error(e.message); })
            .then(() => this.setState({ agentSaving: false }));
    };

    renderAgent() {
        const { agent } = this.state;
        if (!agent || (!agent.linked && !agent.canAdd)) return null;
        if (agent.linked) {
            return <p className="b-agent-line"><i className="pi pi-user" /> حسابك مسجّل عن طريق الوكيل <b dir="ltr">{agent.code}</b></p>;
        }
        return (
            <section className="b-card b-agent">
                <span className="b-agent-ic" aria-hidden="true"><i className="pi pi-id-card" /></span>
                <div className="b-agent-tx">
                    <h3>عندك كود وكيل؟</h3>
                    <p className="b-hint">لو حد من وكلاء المنصة عرّفك علينا اكتب الكود بتاعه هنا. ينفع مرة واحدة بس، وقبل أول شحن للمحفظة.</p>
                </div>
                <div className="b-agent-form">
                    <InputText dir="ltr" maxLength={16} placeholder="A0007" value={this.state.agentCode}
                        aria-label="كود الوكيل"
                        onChange={(e) => this.setState({ agentCode: e.target.value.toUpperCase() })} />
                    <button type="button" className="ui-btn is-secondary"
                        disabled={this.state.agentSaving || !this.state.agentCode.trim()} onClick={this.saveAgent}>
                        {this.state.agentSaving ? 'جاري الحفظ...' : 'حفظ الكود'}
                    </button>
                </div>
            </section>
        );
    }

    load = () => Promise.all([
        apiFetch('/billing/me'),
        apiFetch('/billing/calculator'),
        apiFetch('/billing/wallet/transactions'),
        apiFetch('/billing/topups'),
        apiFetch('/billing/upgrades'),
    ]).then(([status, config, transactions, topUps, upgrades]) => {
        this.setState({
            status, config, transactions, topUps, upgrades, loading: false,
            renewMonths: Math.max(config.minMonths || 1, this.state.renewMonths),
        });
    }).catch(e => { this.setState({ loading: false }); this.error(e.message); });

    ok = (detail) => this.toast.current.show({ severity: 'success', summary: 'تم بنجاح', detail });
    error = (detail) => this.toast.current.show({ severity: 'error', summary: 'لم تتم العملية', detail: detail || 'خطأ في الإتصال', life: 6000 });

    openTopUp = (amount) => this.setState({ topUpOpen: true, topUpAmount: amount || null });

    // ---------- renew / auto-renew ----------
    renew = () => {
        const { status, renewMonths, renewPromo } = this.state;
        const total = Number(status.monthlyUsd) * renewMonths;
        const promoCode = renewPromo.trim() || null;
        // with a code the exact price is only known to the server; without one the wallet must cover the full total
        if (!promoCode && Number(status.balanceUsd) < total) { this.openTopUp(total - Number(status.balanceUsd)); return; }
        const ask = promoCode
            ? `هيتخصم من محفظتك ${usd(total)} ناقص خصم الكود ${promoCode}، ويتمد اشتراكك ${renewMonths} شهر.`
            : `هيتخصم ${usd(total)} من محفظتك ويتمد اشتراكك ${renewMonths} شهر.`;
        this.setState({
            ask: {
                title: 'تجدّد اشتراكك؟',
                note: ask,
                icon: 'pi pi-refresh',
                confirmLabel: 'أيوه، جدّد',
                busyKey: 'renewing',
                run: () => this.doRenew(renewMonths, promoCode),
            },
        });
    }

    doRenew = (renewMonths, promoCode) => {
        this.setState({ renewing: true });
        apiFetch('/billing/renew', { method: 'POST', body: { months: renewMonths, promoCode } })
            .then(() => { this.setState({ renewing: false, renewPromo: '', ask: null }); this.ok('تم تجديد الاشتراك'); return this.load(); })
            .catch(e => { this.setState({ renewing: false, ask: null }); this.error(e.message); });
    }

    startTrial = () => {
        this.setState({ startingTrial: true });
        apiFetch('/billing/trial', { method: 'POST' })
            .then(() => { this.setState({ startingTrial: false }); this.ok('بدأت تجربتك المجانية'); return this.load(); })
            .catch(e => { this.setState({ startingTrial: false }); this.error(e.message); });
    }

    toggleAutoRenew = (autoRenew) => {
        apiFetch('/billing/auto-renew', { method: 'PATCH', body: { autoRenew } })
            .then(status => { this.setState({ status }); this.ok(autoRenew ? 'تم تفعيل التجديد التلقائي' : 'تم إيقاف التجديد التلقائي'); return this.load(); })
            .catch(e => this.error(e.message));
    }

    // ---------- upgrade ----------
    openUpgrade = () => this.setState({
        upgradeOpen: true, upgrade: { addStorageGb: 0, addStudents: 0, addVideos: 0, note: '' }, upgradeQuote: null, upgradeError: null,
    })

    setUpgrade = (key, value) => {
        const upgrade = { ...this.state.upgrade, [key]: value == null ? 0 : value };
        this.setState({ upgrade }, () => {
            clearTimeout(this.upgradeTimer);
            if (key !== 'note') this.upgradeTimer = setTimeout(this.quoteUpgrade, 250);
        });
    }

    quoteUpgrade = () => {
        const { addStorageGb, addStudents, addVideos } = this.state.upgrade;
        if (addStorageGb + addStudents + addVideos <= 0) { this.setState({ upgradeQuote: null, upgradeError: null }); return; }
        apiFetch('/billing/upgrades/quote', { method: 'POST', body: { addStorageGb, addStudents, addVideos } })
            .then(upgradeQuote => this.setState({ upgradeQuote, upgradeError: null }))
            .catch(e => this.setState({ upgradeQuote: null, upgradeError: e.message }));
    }

    sendUpgrade = () => {
        const { upgrade } = this.state;
        if (upgrade.addStorageGb + upgrade.addStudents + upgrade.addVideos <= 0) { this.setState({ upgradeError: 'حدد الزيادة اللي محتاجها' }); return; }
        this.setState({ upgradeSending: true });
        apiFetch('/billing/upgrades', { method: 'POST', body: upgrade })
            .then(() => { this.setState({ upgradeSending: false, upgradeOpen: false }); this.ok('تم إرسال طلب الزيادة للإدارة'); return this.load(); })
            .catch(e => this.setState({ upgradeSending: false, upgradeError: e.message }));
    }

    // ---------- render ----------
    meter(label, icon, tone, used, limit, fmt, i) {
        const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
        return (
            <div className={`b-meter ${tone}${pct >= 90 ? ' hot' : ''}`} key={label} style={{ '--i': i }}>
                <span className="b-meter-wm" aria-hidden="true"><i className={icon} /></span>
                <span className="b-meter-k">{label}</span>
                <span className="b-meter-v" dir="ltr">{fmt(used)} <small>/ {fmt(limit)}</small></span>
                <span className="b-bar" aria-hidden="true"><span className="b-barfill" style={{ '--w': `${pct}%` }} /></span>
                <span className="b-meter-pct">{pct}%</span>
            </div>
        );
    }

    /** The three things a teacher opens this page to know: where the plan stands, the money, and what a month costs. */
    renderOverview() {
        const { status: s } = this.state;
        const negative = Number(s.balanceUsd) < 0;
        return (
            <section className="b-overview">
                <div className="b-tile b-tile-plan" style={{ '--i': 0 }}>
                    <span className="b-tile-ic" aria-hidden="true"><i className="pi pi-verified" /></span>
                    <span className="b-tile-k">اشتراكك</span>
                    <span className="b-tile-chips">
                        <Chip label={STATE_LABEL[s.state]} severity={STATE_SEVERITY[s.state]} />
                        {s.trial && <Chip label="تجربة مجانية" severity="info" />}
                    </span>
                    {s.state !== 'NONE' ? (
                        <span className="b-tile-sub">
                            ينتهي في <b dir="ltr">{day(s.endsAt)}</b>
                            {s.state === 'ACTIVE' && <> · باقي <b>{s.daysLeft}</b> يوم</>}
                        </span>
                    ) : (
                        <span className="b-tile-sub">لسه ما اخترتش باقة</span>
                    )}
                </div>

                <div className={`b-tile b-tile-wallet${negative ? ' is-neg' : ''}`} style={{ '--i': 1 }}>
                    <span className="b-tile-ic" aria-hidden="true"><i className="pi pi-wallet" /></span>
                    <span className="b-tile-k">رصيد المحفظة</span>
                    <strong className="b-balance" dir="ltr"><CountUp to={s.balanceUsd} format={usd} /></strong>
                    <span className="b-tile-sub">≈ {egp(s.balanceEgp)}</span>
                    {negative && <span className="b-tile-alert">عليك مديونية — أول شحن هيسددها الأول</span>}
                    {s.pendingTopUps > 0 && <span className="b-tile-sub">عندك {s.pendingTopUps} طلب شحن قيد المراجعة</span>}
                    <button type="button" className="ui-btn is-gold b-tile-cta" onClick={() => this.openTopUp()}>
                        <i className="pi pi-plus" /> شحن المحفظة
                    </button>
                </div>

                {s.state !== 'NONE' && (
                    <div className="b-tile b-tile-cost" style={{ '--i': 2 }}>
                        <span className="b-tile-ic" aria-hidden="true"><i className="pi pi-calendar" /></span>
                        <span className="b-tile-k">تكلفة الشهر بالأسعار الحالية</span>
                        <strong className="b-cost" dir="ltr">{usd(s.monthlyUsd)}</strong>
                        <span className="b-tile-sub">≈ {egp(s.monthlyEgp)}</span>
                        <label className="b-autorenew" htmlFor="auto-renew">
                            <InputSwitch inputId="auto-renew" checked={s.autoRenew} onChange={e => this.toggleAutoRenew(e.value)} />
                            <span>التجديد التلقائي من المحفظة كل شهر</span>
                        </label>
                    </div>
                )}
            </section>
        );
    }

    renderStatus() {
        const { status, renewMonths, renewing } = this.state;
        const s = status;
        const renewTotal = Number(s.monthlyUsd) * renewMonths;
        return (
            <section className="b-card b-status">
                <header className="b-cardhead">
                    <h3><i className="pi pi-chart-pie" /> استهلاكك من الباقة</h3>
                </header>

                {s.message && (
                    <div className={`ui-alert ${s.state === 'SUSPENDED' ? 'is-bad' : 'is-warn'} b-message`}>
                        <i className={s.state === 'SUSPENDED' ? 'pi pi-times-circle' : 'pi pi-exclamation-triangle'} />
                        <span>{s.message}</span>
                    </div>
                )}

                <div className="b-meters">
                    {this.meter('المساحة (جيجا)', 'pi pi-database', 'is-gold', Number(gb(s.usedStorageBytes)), s.storageGb, v => String(v), 0)}
                    {this.meter('الطلبة المفعّلين', 'pi pi-users', 'is-teal', s.activeStudents, s.students, String, 1)}
                    {this.meter('الفيديوهات', 'pi pi-video', 'is-red', s.videosUsed, s.videos, String, 2)}
                </div>

                {s.cdnMonthBytes > 0 && (
                    <div className="b-row">
                        <span>
                            استهلاك الـ CDN هذا الشهر
                            <small className="b-muted">وقت الضغط العالي بتتبث فيديوهاتك من شبكة أسرع، وبيتخصم استهلاكها من محفظتك كل 12 ساعة</small>
                        </span>
                        <b dir="ltr">{(s.cdnMonthBytes / 1e9).toFixed(2)} GB — {usd(s.cdnMonthUsd)}</b>
                    </div>
                )}

                {/* renew on one side, ask for more on the other */}
                <div className="b-actions">
                    <div className="b-renew">
                        <span className="b-renew-k"><i className="pi pi-refresh" /> جدّد لمدة</span>
                        <InputNumber inputId="renew-months" value={renewMonths} showButtons min={this.state.config.minMonths} max={this.state.config.maxMonths}
                            aria-label="مدة التجديد بالشهور"
                            suffix=" شهر" onValueChange={e => this.setState({ renewMonths: e.value || this.state.config.minMonths })} />
                        <InputText value={this.state.renewPromo} dir="ltr" maxLength={50} placeholder="كود خصم" className="b-renew-promo"
                            aria-label="كود خصم للتجديد" onChange={e => this.setState({ renewPromo: e.target.value.toUpperCase() })} />
                        <button type="button" className="ui-btn is-gold" disabled={renewing} onClick={this.renew}>
                            <i className={renewing ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'} />
                            {renewing ? 'جاري التجديد...' : <>تجديد — <span dir="ltr">{usd(renewTotal)}</span></>}
                        </button>
                    </div>
                    <div className="b-more">
                        <button type="button" className="ui-btn is-secondary"
                            disabled={s.pendingUpgrade || s.state !== 'ACTIVE' || s.trial} onClick={this.openUpgrade}>
                            <i className="pi pi-arrow-up" /> {s.pendingUpgrade ? 'طلب الزيادة قيد المراجعة' : 'طلب زيادة'}
                        </button>
                        {s.state === 'SUSPENDED' &&
                            <button type="button" className="b-linkbtn" onClick={() => this.setState({ showCalculator: true })}>أو اختار باقة جديدة</button>}
                    </div>
                </div>
            </section>
        );
    }

    renderHistory() {
        const { transactions, topUps, upgrades, tab } = this.state;
        const tabs = [
            { label: 'حركات المحفظة', icon: 'pi pi-arrow-right-arrow-left', n: transactions.length },
            { label: 'طلبات الشحن', icon: 'pi pi-wallet', n: topUps.length },
            { label: 'طلبات الزيادة', icon: 'pi pi-arrow-up', n: upgrades.length },
        ];
        const empty = (text) => (
            <div className="b-empty">
                <span className="b-empty-ic" aria-hidden="true"><i className="pi pi-inbox" /></span>
                <p>{text}</p>
            </div>
        );

        return (
            <section className="b-card b-history">
                <header className="b-cardhead">
                    <h3><i className="pi pi-history" /> السجل</h3>
                </header>

                <div className="b-tabs" role="tablist" aria-label="السجل">
                    {tabs.map((t, i) => (
                        <button key={t.label} type="button" role="tab" id={`b-tab-${i}`} aria-controls={`b-panel-${i}`}
                            aria-selected={tab === i} className={`b-tab${tab === i ? ' is-on' : ''}`}
                            onClick={() => this.setState({ tab: i })}>
                            <i className={t.icon} /> {t.label} <span className="b-tab-n">{t.n}</span>
                        </button>
                    ))}
                </div>

                <div className="b-panel" role="tabpanel" id={`b-panel-${tab}`} aria-labelledby={`b-tab-${tab}`} key={tab}>
                    {tab === 0 && (transactions.length === 0 ? empty('لا يوجد حركات لسه') : (
                        <div className="b-table-wrap">
                            <table className="b-table">
                                <thead><tr><th>التاريخ</th><th>النوع</th><th>المبلغ</th><th>الرصيد بعدها</th><th>البيان</th></tr></thead>
                                <tbody>{transactions.map(t => (
                                    <tr key={t.id}>
                                        <td dir="ltr">{day(t.createdAt)}</td>
                                        <td><span className="b-type">{TX_LABEL[t.type] || t.type}</span></td>
                                        <td dir="ltr" className={Number(t.amountUsd) < 0 ? 'neg' : 'pos'}>{Number(t.amountUsd) > 0 ? '+' : ''}{usd(t.amountUsd)}</td>
                                        <td dir="ltr">{usd(t.balanceAfterUsd)}</td>
                                        <td className="b-note">{t.note}</td>
                                    </tr>
                                ))}</tbody>
                            </table>
                        </div>
                    ))}

                    {tab === 1 && (topUps.length === 0 ? empty('لا يوجد طلبات') : (
                        <div className="b-table-wrap">
                            <table className="b-table">
                                <thead><tr><th>التاريخ</th><th>المبلغ</th><th>الطريقة</th><th>رقم العملية</th><th>الحالة</th></tr></thead>
                                <tbody>{topUps.map(r => (
                                    <tr key={r.id}>
                                        <td dir="ltr">{day(r.createdAt)}</td>
                                        <td dir="ltr">{usd(r.amountUsd)}<div className="b-muted">{egp(r.amountEgp)}</div></td>
                                        <td>{r.method}</td>
                                        <td dir="ltr">{r.reference || '—'}</td>
                                        <td><Chip label={REQUEST_LABEL[r.status]} severity={REQUEST_SEVERITY[r.status]} />
                                            {r.adminNote && <div className="b-note">{r.adminNote}</div>}</td>
                                    </tr>
                                ))}</tbody>
                            </table>
                        </div>
                    ))}

                    {tab === 2 && (upgrades.length === 0 ? empty('لا يوجد طلبات') : (
                        <div className="b-table-wrap">
                            <table className="b-table">
                                <thead><tr><th>التاريخ</th><th>الزيادة</th><th>التكلفة</th><th>الحالة</th></tr></thead>
                                <tbody>{upgrades.map(r => (
                                    <tr key={r.id}>
                                        <td dir="ltr">{day(r.createdAt)}</td>
                                        <td>{[r.addStorageGb && `+${r.addStorageGb} جيجا`, r.addStudents && `+${r.addStudents} طالب`, r.addVideos && `+${r.addVideos} فيديو`].filter(Boolean).join('، ')}</td>
                                        <td dir="ltr">{usd(r.chargedUsd != null ? r.chargedUsd : r.quotedUsd)}</td>
                                        <td><Chip label={REQUEST_LABEL[r.status]} severity={REQUEST_SEVERITY[r.status]} />
                                            {r.adminNote && <div className="b-note">{r.adminNote}</div>}</td>
                                    </tr>
                                ))}</tbody>
                            </table>
                        </div>
                    ))}
                </div>
            </section>
        );
    }

    renderUpgradeDialog() {
        const { upgradeOpen, upgrade, upgradeQuote, upgradeError, upgradeSending, status } = this.state;
        const rows = [['addStorageGb', 'مساحة إضافية', ' جيجا', 'pi pi-database'], ['addStudents', 'طلبة إضافيين', ' طالب', 'pi pi-users'], ['addVideos', 'فيديوهات إضافية', ' فيديو', 'pi pi-video']];
        const footer = (
            <div className="b-dialog-foot">
                <button type="button" className="ui-btn is-secondary" onClick={() => this.setState({ upgradeOpen: false })} disabled={upgradeSending}>إلغاء</button>
                <button type="button" className="ui-btn is-gold" onClick={this.sendUpgrade} disabled={upgradeSending}>
                    <i className={upgradeSending ? 'pi pi-spin pi-spinner' : 'pi pi-send'} /> {upgradeSending ? 'جاري الإرسال...' : 'إرسال الطلب'}
                </button>
            </div>
        );
        return (
            <Dialog header="طلب زيادة" visible={upgradeOpen} onHide={() => this.setState({ upgradeOpen: false })} footer={footer} className="billing-dialog" dismissableMask>
                <p className="b-hint">الفرق بيتحسب على الأيام الباقية من اشتراكك، وبيتخصم من محفظتك لما الإدارة توافق.</p>
                {rows.map(([key, label, suffix, icon]) => (
                    <div className="b-field" key={key}>
                        <label htmlFor={`up-${key}`}><i className={icon} /> {label}</label>
                        <InputNumber inputId={`up-${key}`} value={upgrade[key]} min={0} suffix={suffix} showButtons
                            onChange={e => this.setUpgrade(key, e.value)} />
                    </div>
                ))}
                <div className="b-field">
                    <label htmlFor="up-note">ملاحظة (اختياري)</label>
                    <InputText id="up-note" value={upgrade.note} maxLength={500} onChange={e => this.setUpgrade('note', e.target.value)} />
                </div>
                {upgradeQuote && (
                    <div className="b-quote">
                        <div>التكلفة: <b dir="ltr">{usd(upgradeQuote.costUsd)}</b> ({egp(upgradeQuote.costEgp)}) عن {upgradeQuote.remainingDays} يوم</div>
                        <div className="b-muted">الشهر بعد الزيادة هيبقى {usd(upgradeQuote.newMonthlyUsd)}</div>
                        {Number(status.balanceUsd) < Number(upgradeQuote.costUsd) &&
                            <div className="b-error">رصيدك مش كفاية. اشحن المحفظة قبل ما الإدارة توافق.</div>}
                    </div>
                )}
                {upgradeError && <p className="b-error" role="alert">{upgradeError}</p>}
            </Dialog>
        );
    }

    render() {
        const { loading, status, config, showCalculator, topUpOpen, topUpAmount, supportWhatsapp, ask } = this.state;
        const needsPlan = status && (status.state === 'NONE' || status.trial || showCalculator);
        return (
            <>
                <Header pageName="اشتراكي" />
                <Toast ref={this.toast}></Toast>
                <div className="content billing-page">
                    <div className="container">
                        {loading && (
                            <div className="b-skeleton" aria-hidden="true">
                                <span className="s-row"><i /><i /><i /></span>
                                <i className="s-card" />
                                <i className="s-card" />
                            </div>
                        )}
                        {!loading && status && config && (
                            <>
                                {this.renderOverview()}

                                {status.trialOffer && (
                                    <section className="b-trial">
                                        <span className="b-trial-ic" aria-hidden="true"><i className="pi pi-gift" /></span>
                                        <div className="b-trial-tx">
                                            <h3>جرّب المنصة مجانًا {status.trialOffer.days} يوم</h3>
                                            <p>
                                                {status.trialOffer.storageGb} جيجا مساحة، {status.trialOffer.students} طالب، و{status.trialOffer.videos} فيديو — من غير أي دفع.
                                                التجربة مرة واحدة بس، وتقدر تشترك في أي وقت أثناءها.
                                            </p>
                                        </div>
                                        <button type="button" className="ui-btn is-gold" disabled={this.state.startingTrial} onClick={this.startTrial}>
                                            <i className={this.state.startingTrial ? 'pi pi-spin pi-spinner' : 'pi pi-play'} />
                                            {this.state.startingTrial ? 'جاري التفعيل...' : 'ابدأ التجربة المجانية'}
                                        </button>
                                    </section>
                                )}

                                {needsPlan && (
                                    <section className="b-card b-plan">
                                        <header className="b-cardhead">
                                            <h3><i className="pi pi-sliders-h" /> {status.trial ? 'اشترك قبل ما التجربة تخلص' : 'اختار باقتك'}</h3>
                                        </header>
                                        <p className="b-hint">حدد المدة والمساحة وعدد الطلبة والفيديوهات اللي محتاجهم، والسعر بيتحسب على طول.</p>
                                        <Calculator config={config} balanceUsd={status.balanceUsd}
                                            initial={status.storageGb ? { storageGb: status.storageGb, students: status.students, videos: status.videos } : null}
                                            onNeedTopUp={this.openTopUp}
                                            onSubscribed={() => { this.setState({ showCalculator: false }); this.ok('تم الاشتراك بنجاح'); this.load(); }}
                                            onError={this.error} />
                                    </section>
                                )}

                                {status.state !== 'NONE' && this.renderStatus()}
                                {this.renderAgent()}
                                {this.renderHistory()}
                                {this.renderUpgradeDialog()}
                                <TopUpDialog visible={topUpOpen} suggestedAmount={topUpAmount} rate={config.usdEgpRate} supportWhatsapp={supportWhatsapp}
                                    onHide={() => this.setState({ topUpOpen: false })}
                                    onSent={() => { this.setState({ topUpOpen: false }); this.ok('تم إرسال طلب الشحن للإدارة'); this.load(); this.loadAgent(); }} />

                                <Confirm
                                    visible={!!ask}
                                    onHide={() => { if (!(ask && this.state[ask.busyKey])) this.setState({ ask: null }); }}
                                    tone="warn"
                                    icon={ask && ask.icon}
                                    title={ask && ask.title}
                                    note={ask && ask.note}
                                    confirmLabel={ask ? ask.confirmLabel : ''}
                                    busy={!!(ask && this.state[ask.busyKey])}
                                    busyLabel="لحظة…"
                                    onConfirm={() => ask && ask.run()}
                                />
                            </>
                        )}
                    </div>
                </div>
            </>
        );
    }
}

export default Billing;
